import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { HoneypotInputs } from 'remix-utils/honeypot/react'

import { type ListOfErrors } from '#app/components/form/types.ts'
import { MAGIC_LINK_SUPPORT_MESSAGE } from '#app/domains/auth/login.constants.ts'
import { type Route } from '#app/routes/_auth+/+types/login.ts'
import { MagicLinkSchema } from '#app/routes/_auth+/login.schema.ts'
import { useIsPending } from '#app/utils/misc.tsx'

type LoginFormControllerOptions = {
	actionData: Route.ComponentProps['actionData']
	redirectTo: string
}

export type LoginFormViewModel = {
	formProps: ReturnType<typeof getFormProps>
	email: {
		inputProps: ReturnType<typeof getInputProps>
		errors: string[]
	}
	redirectInputProps: ReturnType<typeof getInputProps>
	isPending: boolean
	devVerifyUrl: string | null
	formErrors: ListOfErrors
}

export function useLoginFormController({
	actionData,
	redirectTo,
}: LoginFormControllerOptions): LoginFormViewModel {
	const isPending = useIsPending()
	const [form, fields] = useForm({
		id: 'magic-login-form',
		constraint: getZodConstraint(MagicLinkSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: MagicLinkSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	const emailErrors = fields.email.errors ?? []
	const emailInputProps = getInputProps(fields.email, { type: 'email' })
	const redirectInputProps = getInputProps(fields.redirectTo, {
		type: 'hidden',
	})

	const devVerifyUrl =
		actionData?.result?.status === 'success' &&
		actionData.result.initialValue &&
		typeof actionData.result.initialValue === 'object' &&
		'devVerifyUrl' in actionData.result.initialValue &&
		typeof actionData.result.initialValue.devVerifyUrl === 'string'
			? actionData.result.initialValue.devVerifyUrl
			: null

	const formErrors: ListOfErrors =
		form.errors?.map((error) => {
			if (!error) return error
			const normalized = error.trim()
			if (normalized !== MAGIC_LINK_SUPPORT_MESSAGE) return error
			const target = 'support'
			const index = normalized.toLowerCase().lastIndexOf(target)
			if (index < 0) return error
			const before = normalized.slice(0, index)
			const after = normalized.slice(index + target.length)
			const word = normalized.slice(index, index + target.length)
			return (
				<span key="error-message">
					{before}
					<a
						href="mailto:stan@lydra.ai"
						className="underline underline-offset-2"
					>
						{word}
					</a>
					{after}
				</span>
			)
		}) ?? null

	return {
		formProps: getFormProps(form) as ReturnType<typeof getFormProps>,
		email: {
			inputProps: emailInputProps,
			errors: emailErrors,
		},
		redirectInputProps,
		isPending,
		devVerifyUrl,
		formErrors,
	}
}

export { HoneypotInputs }
